import { route } from 'quasar/wrappers';
import { createRouter, createMemoryHistory, createWebHistory, createWebHashHistory } from 'vue-router';
import routes from './routes.js';
import { isPermittedView, isDefaultView } from 'src/js/userdata.js';
import { isToken, authByToken, doesPermittedUserExist } from 'src/js/auth.js';
import { canUseCansativa } from 'src/js/utils.js';
import { Notify } from 'quasar';

import stylesheet from 'src/middleware/spreadsheetMiddleware';

/*
 * If not building with SSR mode, you can
 * directly export the Router instantiation;
 *
 * The function below can be async too; either use
 * async/await or return a Promise which resolves
 * with the Router instance.
 */

export default route(function ({ store /*, ssrContext*/ }) {
  const createHistory = process.env.SERVER
    ? createMemoryHistory
    : process.env.VUE_ROUTER_MODE === 'history'
    ? createWebHistory
    : createWebHashHistory;

  const Router = createRouter({
    scrollBehavior: () => ({ left: 0, top: 0 }),
    routes,
    // Leave this as is and make changes in quasar.conf.js instead!
    // quasar.conf.js -> build -> vueRouterMode
    // quasar.conf.js -> build -> publicPath
    history: createHistory(process.env.MODE === 'ssr' ? void 0 : process.env.VUE_ROUTER_BASE)
    // history: false
  });

  // The cart and the order history must not open by typing the URL either.
  // Users who may not order are sent to the normal start page.
  const START_PAGE = '/crm/inventoryv2?type=buds';
  const isCansativaPageAndNotAllowed = (to, user) =>
    to.matched.some((route) => route.meta?.requiresCansativa) && !canUseCansativa(user);

  Router.beforeEach(async (to, from, next) => {
    const cachedUser = store?._state?.data?.user?.cu;
    if (cachedUser?.id) {
      if (isCansativaPageAndNotAllowed(to, cachedUser)) {
        return next(START_PAGE);
      }

      stylesheet(to, from, next);

      return next();
    }

    if (to.path !== '/auth/login' && to.path !== '/download' && !to.path.startsWith('/confirm-delivery')) {
      if (store._state.data.user.status === 'active') {
        if (isPermittedView(to.path, store._state.data.user.cu.role) === false) {
          return next({ path: '/auth/login', query: { removeToken: true } });
        }
        if (isCansativaPageAndNotAllowed(to, store._state.data.user.cu)) {
          return next(START_PAGE);
        }
      } else if (isToken()) {
        let userValidation = await authByToken();

        if ((await userValidation) !== false) {
          if (userValidation?.main_pharmacy) {
            userValidation.original_id = userValidation.id;
            userValidation.id = userValidation.main_pharmacy;
          }

          store.commit('user/cu', { ...userValidation });

          if (isPermittedView(to.path, store._state.data.user.cu.role) === false) {
            return next({ path: '/auth/login', query: { removeToken: true } });
          }
          if (isCansativaPageAndNotAllowed(to, store._state.data.user.cu)) {
            return next(START_PAGE);
          }
          if (to.path === '/crm' || to.path === '/' || to.path === '/crm/inventoryv2') {
            return next('/crm/inventoryv2?type=buds');
          }
        } else {
          Notify.create({
            type: 'negative',
            caption: 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.'
          });

          return next({ path: '/auth/login', query: { removeToken: true } });
        }
      } else {
        if (isDefaultView(to.path) === false) {
          return next({ path: '/auth/login', query: { removeToken: true } });
        }
      }
    }

    stylesheet(to, from, next);

    next();
  });

  return Router;
});
